import cn from 'clsx'
import React, { useCallback, useEffect } from 'react'
import { DropTarget } from 'react-dnd'
import * as ItemTypes from '@disco/constants/ItemTypes'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import { DropFilesIndicator } from 'components/ui/drop-indicator'
import useTimeout from 'hooks/use-timeout'
import { isReadOnlyTrack } from '@disco/utils/tracks'
const playlistTarget = {
  canDrop(props, monitor) {
    const item = monitor.getItem()
    if (props.playlist.playlist_id === item.sourcePlaylistId) return false

    if (item.track) {
      const readOnlyTrack = isReadOnlyTrack(item.track)
      return !!item.track.uploaded && !readOnlyTrack
    }

    if (item.files) return true
    return false
  },

  drop(props, monitor) {
    const draggedItem = monitor.getItem()
    // ignore items dragged from the same playlist
    if (draggedItem.sourcePlaylistId === props.playlist.playlist_id) return
    // don't handle drop if child already handle it
    if (monitor.didDrop()) return

    if (props.onDrop) {
      // if onDrop is defined pass let it process dropped items
      props.onDrop(props.playlist, draggedItem)
    } else {
      if (draggedItem.items) {
        getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
          if (files.length > 0) {
            props.uploadFilesToPlaylist(files, props.playlist)
          }
        })
      }

      if (draggedItem.track && draggedItem.track.uploaded) {
        const trackSelected =
          props.selectedItems.length > 0 &&
          isDraggedItemPartOfSelection(props.selectedItems, draggedItem)

        if (!trackSelected) {
          props.appendTracksToPlaylist(props.playlist, [draggedItem.track])
        } else {
          const tracks = props.selectedItems.filter(
            item => item.type === ItemTypes.TRACK,
          )

          if (tracks.length > 0) {
            props.appendTracksToPlaylist(props.playlist, tracks)
          }
        }
      }
    }
  },
}

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver({
      shallow: true,
    }),
    canDrop: monitor.canDrop(),
    draggedItem: monitor.getItem(),
  }
}

type Props = {
  isOver: boolean
  canDrop?: boolean
  connectDropTarget: (...args: Array<any>) => any
  children?: React.ReactNode
  openPane?: () => void
  draggedItem: Record<string, any> | null | undefined
}

const PlaylistDropWrapper = ({
  canDrop,
  children,
  connectDropTarget,
  isOver,
  openPane,
  draggedItem,
}: Props) => {
  const [willOpen, setWillOpen] = React.useState(false)
  const openPaneCallback = useCallback(() => {
    if (isOver && openPane) openPane()
  }, [openPane, isOver])
  useTimeout(() => openPaneCallback(), openPane && willOpen ? 1000 : null)
  useEffect(() => {
    if (openPane) setWillOpen(isOver && canDrop)
  }, [isOver, canDrop, openPane])
  const dropTargetClass = cn({
    HighlightedDropTarget: isOver && canDrop,
    'HighlightedDropTarget--blink': willOpen,
  })
  return connectDropTarget(
    <div className={dropTargetClass}>
      {children}
      {isOver && draggedItem?.files && <DropFilesIndicator />}
    </div>,
  )
}

export default DropTarget(
  [ItemTypes.TRACK, ItemTypes.FILE],
  playlistTarget,
  collect,
)(PlaylistDropWrapper) as any
